import { useEffect } from 'react';

import { useLocation } from '@/hooks/useRouterSafe';
import { prefetchManager } from '@/utils/prefetch-manager';

/**
 * Hook to automatically prefetch route chunks based on current location
 * Improves perceived performance by loading likely next routes
 */
export function usePrefetchRoute() {
  const location = useLocation();

  useEffect(() => {
    // Only prefetch on stable connection
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection?.saveData || connection?.effectiveType === '2g') {
        return; // Skip on data saver or very slow connections
      }
    }

    // Use requestIdleCallback for non-blocking prefetch
    const schedulePrefetch = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(
          () => {
            prefetchManager.startPrefetch(location.pathname);
          },
          { timeout: 5000 },
        );
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          prefetchManager.startPrefetch(location.pathname);
        }, 2000);
      }
    };

    // Schedule prefetch after a short delay to prioritize current page
    const timeoutId = setTimeout(schedulePrefetch, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [location.pathname]);
}

/**
 * Manual prefetch hook for specific user interactions
 * Use this when you know user is likely to navigate somewhere
 */
export function useManualPrefetch() {
  const prefetchRoute = (path: string) => {
    // Immediate prefetch for user-initiated actions
    prefetchManager.startPrefetch(path);
  };

  const prefetchAnalytics = () => {
    // Prefetch all analytics chunks
    import('@/pages/analytics/InsightsPage');
  };

  const prefetchStudio = () => {
    import('@/pages/create/CreatePage');
  };

  return {
    prefetchRoute,
    prefetchAnalytics,
    prefetchStudio,
  };
}
